import { ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from "react";
import { Modal, Button, CardMedia, Input, CircularProgress, Select, MenuItem } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import EditText from "../components/editText";
import SwitchToggle from "../components/SwitchToggle";
import { useAddBrandMutation, useUpdateBrandMutation, useUploadMediaMutation, useGetCategoriesQuery } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  brandData?: any;
  defaultType?: number;
}

export default function AddBrand({
  open,
  onClose,
  setOpen,
  brandData,
  defaultType,
}: ModalProps) {
  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [description, setDescription] = useState("");
  const [isPopular, setIsPopular] = useState(false);
  const [isTopBrand, setIsTopBrand] = useState(false);
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { data: categoriesData } = useGetCategoriesQuery({ 
    search: "", 
    page: 1, 
    limit: 100,
    type: defaultType || (selectedVehicleTypes.length > 0 ? selectedVehicleTypes[0] : undefined)
  });

  const [addBrand, { isLoading: isAdding }] = useAddBrandMutation();
  const [updateBrand, { isLoading: isUpdating }] = useUpdateBrandMutation();
  const [uploadMedia] = useUploadMediaMutation();

  const toggleOpen = () => {
    setOpen(!open);
  }

  useEffect(() => {
    if (open) {
      if (brandData) {
        setName(brandData.name || "");
        setImage(brandData.logo || "");
        setDescription(brandData.description || "");
        const types = brandData.type;
        setSelectedVehicleTypes(Array.isArray(types) ? types : (types ? [types] : []));

        let catIds: string[] = [];
        if (brandData.categories && Array.isArray(brandData.categories)) {
          catIds = brandData.categories.map((c: any) => c._id);
        } else if (brandData.categoryId) {
          catIds = Array.isArray(brandData.categoryId) ? brandData.categoryId : [brandData.categoryId];
        } else if (brandData.category) {
          // Handle if it comes as object or array of objects similar to other modals
          const temp = Array.isArray(brandData.category) ? brandData.category : [brandData.category];
          catIds = temp.map((c: any) => c._id || c);
        }
        setSelectedCategories(catIds);

        setIsPopular(brandData.isPopular === true);
        setIsTopBrand(brandData.isTopBrand === true);
      } else {
        setName("");
        setImage("");
        setDescription("");
        setSelectedVehicleTypes(defaultType ? [defaultType] : []);
        setSelectedCategories([]);
        setIsPopular(false);
        setIsTopBrand(false);
      }
    }
  }, [open, brandData, defaultType]);

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format not supported (Only JPG, JPEG, and PNG)");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      setIsUploading(true);
      try {
        const res = await uploadMedia(formData).unwrap();
        const uploadedUrl = res?.data?.url || res?.url || res?.data;
        if (uploadedUrl) {
          setImage(uploadedUrl);
          toast.success("Logo uploaded successfully");
        }
      } catch (err: any) {
        toast.error(err?.data?.message || "Logo upload failed");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number[];
    setSelectedVehicleTypes(value);
  };

  const handleCategoryChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedCategories(value);
  };

  const handleSave = async () => {
    if (!image) {
      toast.error("Logo is required");
      return;
    }
    if (!name.trim()) {
      toast.error("Brand Name is required");
      return;
    }
    if (name.length > 40) {
      toast.error("Brand Name cannot exceed 40 characters");
      return;
    }
    const plainTextDescription = description.replace(/<[^>]*>?/gm, '');
    if (plainTextDescription.length > 500) {
      toast.error("Description cannot exceed 500 characters");
      return;
    }
    if (selectedVehicleTypes.length === 0) {
      toast.error("Vehicle Type is required");
      return;
    }
    if (selectedCategories.length === 0) {
      toast.error("Category is required");
      return;
    }

    try {
      if (brandData?._id) {
        // Edit mode: Send only changed keys
        const payload: any = { id: brandData._id };
        let hasChanges = false;

        if (name !== brandData.name) {
          payload.name = name;
          hasChanges = true;
        }
        if (image !== brandData.logo) {
          payload.logo = image;
          hasChanges = true;
        }
        if (description !== brandData.description) {
          payload.description = description;
          hasChanges = true;
        }
        if (isPopular !== (brandData.isPopular === true)) {
          payload.isPopular = isPopular;
          hasChanges = true;
        }
        if (isTopBrand !== (brandData.isTopBrand === true)) {
          payload.isTopBrand = isTopBrand;
          hasChanges = true;
        }

        // Compare Vehicle Types
        const initialTypes = Array.isArray(brandData.type) ? brandData.type : (brandData.type ? [brandData.type] : []);
        if (JSON.stringify([...selectedVehicleTypes].sort()) !== JSON.stringify([...initialTypes].sort())) {
          payload.type = selectedVehicleTypes;
          hasChanges = true;
        }

        // Compare Categories
        let initialCatIds: string[] = [];
        if (brandData.categories && Array.isArray(brandData.categories)) {
          initialCatIds = brandData.categories.map((c: any) => c._id);
        } else if (brandData.categoryId) {
          initialCatIds = Array.isArray(brandData.categoryId) ? brandData.categoryId : [brandData.categoryId];
        } else if (brandData.category) {
          const temp = Array.isArray(brandData.category) ? brandData.category : [brandData.category];
          initialCatIds = temp.map((c: any) => c._id || c);
        }

        if (JSON.stringify([...selectedCategories].sort()) !== JSON.stringify([...initialCatIds].sort())) {
          payload.categoryId = selectedCategories;
          hasChanges = true;
        }

        if (!hasChanges) {
          toast.info("No changes to save");
          toggleOpen();
          return;
        }

        await updateBrand(payload).unwrap();
        toast.success("Brand updated successfully");
      } else {
        // Add mode: Send all fields
        const payload = {
          name,
          logo: image,
          description,
          isPopular,
          isTopBrand,
          type: selectedVehicleTypes,
          categoryId: selectedCategories,
          order: 1,
        };
        await addBrand(payload).unwrap();
        toast.success("Brand added successfully");
      }
      toggleOpen();
    } catch (err: any) {
      toast.error(err?.data?.message || "Operation failed");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{brandData ? "تعديل علامة تجارية / Edit Brand" : "إضافة علامة تجارية / Add Brand"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={toggleOpen}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="upload-brand-logo">
                  تحميل الشعار / Upload Logo<span className="req">*</span>
                </label>

                {image ? (
                  <div className="upload_image no_radius">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="Brand Logo" />
                      </figure>
                      <CloseIcon
                        className="remove_img"
                        onClick={() => setImage("")}
                      />
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="upload-brand-logo"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="upload-brand-logo"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        {isUploading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <>
                            <img
                              src="/static/images/placeholder.png"
                              alt="Upload"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit"
                              />
                            </span>
                          </>
                        )}
                      </figure>
                    </span>
                  </label>
                )}
              </div>

              <div className="control_group">
                <label htmlFor="brand-name">اسم العلامة التجارية / Brand Name</label>
                <TextField
                  id="brand-name"
                  placeholder="أدخل الاسم / Enter Name"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (input.length > 0) {
                      setName(input.charAt(0).toUpperCase() + input.slice(1).toLowerCase());
                    } else {
                      setName(input);
                    }
                  }}
                  inputProps={{ maxLength: 40 }}
                />
              </div>

              <div className="control_group">
                <label htmlFor="">وصف العلامة التجارية / Brand Description</label>
                <EditText key={brandData?._id || "new"} content={description} setContent={setDescription} />
              </div>

              <div className="control_group">
                <label htmlFor="Category">الفئة / Category</label>
                <Select
                  multiple
                  fullWidth
                  displayEmpty
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  renderValue={(selected) => {
                    const selectedIds = selected as string[];
                    if (selectedIds.length === 0) return <em style={{ color: "#aaa" }}>حدد الفئات / Select Categories</em>;
                    return selectedIds.map(id =>
                      categoriesData?.data?.data?.find((cat: any) => cat._id === id)?.name
                    ).filter(Boolean).join(", ");
                  }}
                >
                  <MenuItem disabled value="">
                    <em>حدد الفئات / Select Categories</em>
                  </MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div className="control_group">
                <label htmlFor="VehicleType">نوع المركبة / Vehicle Type</label>
                <Select
                  multiple
                  fullWidth
                  displayEmpty
                  value={selectedVehicleTypes}
                  onChange={handleVehicleTypeChange}
                  disabled={!!defaultType || !!brandData?._id}
                  renderValue={(selected) => {
                    const selectedIds = selected as number[];
                    if (selectedIds.length === 0) {
                      return <em style={{ color: "#aaa" }}>حدد أنواع المركبات / Select Vehicle Types</em>;
                    }
                    const selectedNames = selectedIds.map((s: number) => {
                      return getVehicleTypeLabel(s);
                    }).filter(Boolean);
                    return selectedNames.join(", ");
                  }}
                >
                  <MenuItem disabled value="">
                    <em>حدد أنواع المركبات / Select Vehicle Types</em>
                  </MenuItem>
                  {Object.keys(VEHICLE_TYPE)
                    .filter(k => isNaN(Number(k)))
                    .map((key) => (
                      <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                        {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                      </MenuItem>
                    ))}
                </Select>
              </div>

              {/* Popular */}
              <div className="control_group flex w_100">
                <label>هل هذه علامة تجارية شائعة؟ / Is this Popular Brand?</label>
                <SwitchToggle
                  checked={isPopular}
                  onChange={(e) => setIsPopular(e.target.checked)}
                />
              </div>

              {/* Top Brand */}
              <div className="control_group flex w_100">
                <label>هل هذه أفضل علامة تجارية؟ / Is this a Top Brand?</label>
                <SwitchToggle
                  checked={isTopBrand}
                  onChange={(e) => setIsTopBrand(e.target.checked)}
                />
              </div>

            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating || isUploading}
              >
                {isAdding || isUpdating ? "جارٍ الحفظ... / Saving..." : "حفظ / Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}